import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain, Lock, Check, Upload, Link as LinkIcon, FileText, Download,
  ChevronDown, ChevronRight, Loader2, ArrowLeft, LogIn, LogOut, Trophy, Sparkles,
  AlertCircle, X, RotateCcw, Eye, Image as ImageIcon
} from "lucide-react";
import { loginWithGoogle as startGoogleLogin } from "../lib/userAuth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const STATUS = {
  locked:    { bg: "bg-zinc-800/50", text: "text-zinc-500", border: "border-zinc-700", label: "Locked",         dot: "bg-zinc-600" },
  pending:   { bg: "bg-zinc-800/30", text: "text-zinc-400", border: "border-zinc-700/50", label: "Not Started",   dot: "bg-zinc-500" },
  submitted: { bg: "bg-amber-500/8", text: "text-amber-400", border: "border-amber-500/20", label: "Under Review", dot: "bg-amber-400" },
  approved:  { bg: "bg-emerald-500/8", text: "text-emerald-400", border: "border-emerald-500/20", label: "Approved",    dot: "bg-emerald-400" },
  rejected:  { bg: "bg-red-500/8",   text: "text-red-400",  border: "border-red-500/20", label: "Needs Revision", dot: "bg-red-400" },
};

export default function AIFounderPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [levels, setLevels] = useState([]);
  const [progress, setProgress] = useState(null);
  const [activeLevel, setActiveLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // { task, submission }

  useEffect(() => {
    const token = localStorage.getItem("ai_tools_token");
    if (token) {
      axios.get(`${API}/user-auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUser(res.data))
        .catch(() => { localStorage.removeItem("ai_tools_token"); })
        .finally(() => setAuthLoading(false));
    } else {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const levelsRes = await axios.get(`${API}/ai-founder/levels`);
        setLevels(levelsRes.data);
        const token = localStorage.getItem("ai_tools_token");
        if (token) {
          try {
            const progressRes = await axios.get(`${API}/ai-founder/progress`, { headers: { Authorization: `Bearer ${token}` } });
            setProgress(progressRes.data);
          } catch {}
        }
      } catch (err) {
        console.error("Failed to load levels:", err);
      } finally {
        setLoading(false);
      }
    };
    if (!authLoading) fetchData();
  }, [authLoading, user]);

  const refreshProgress = useCallback(async () => {
    const token = localStorage.getItem("ai_tools_token");
    if (!token) return;
    try {
      const res = await axios.get(`${API}/ai-founder/progress`, { headers: { Authorization: `Bearer ${token}` } });
      setProgress(res.data);
    } catch {}
  }, []);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    startGoogleLogin('/become-ai-founder');
  };

  const handleLogout = () => {
    const token = localStorage.getItem("ai_tools_token");
    if (token) axios.post(`${API}/user-auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    localStorage.removeItem("ai_tools_token");
    setUser(null);
    setProgress(null);
  };

  const currentLevelOrder = progress?.current_level || 1;
  const isCompleted = progress?.completed || false;

  const getLevelState = (levelOrder) => {
    if (isCompleted) return "completed";
    if (levelOrder < currentLevelOrder) return "completed";
    if (levelOrder === currentLevelOrder) return "active";
    return "locked";
  };

  const getTaskStatus = (taskId) => {
    if (!progress?.submissions) return "pending";
    const sub = progress.submissions[taskId];
    if (!sub) return "pending";
    return sub.status || "pending";
  };

  // Auto-expand current level
  useEffect(() => {
    if (levels.length > 0 && !activeLevel) {
      const current = levels.find(l => l.order === currentLevelOrder);
      if (current) setActiveLevel(current.level_id);
    }
  }, [levels, currentLevelOrder, activeLevel]);

  const totalTasks = levels.reduce((sum, l) => sum + (l.tasks?.length || 0), 0);
  const approvedTasks = Object.values(progress?.submissions || {}).filter(s => s.status === "approved").length;

  if (authLoading) {
    return <div className="min-h-screen bg-[#060608] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#060608] relative">
      {/* Subtle grid pattern */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />

      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-[#060608]/95 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="text-zinc-600 hover:text-white transition-colors" data-testid="back-btn"><ArrowLeft size={18} /></button>
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 bg-[#00F0FF] rounded flex items-center justify-center"><Brain size={13} className="text-black" /></div>
              <span className="font-outfit font-bold text-[#00F0FF] text-sm">ShansoAI<span className="text-white">Hub</span></span>
            </div>
          </div>
          {user ? (
            <div className="flex items-center gap-2.5">
              {user.picture && <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />}
              <span className="text-xs text-zinc-500 font-dm-sans hidden sm:block">{user.name}</span>
              <button onClick={handleLogout} className="text-zinc-600 hover:text-white transition-colors" data-testid="logout-btn"><LogOut size={15} /></button>
            </div>
          ) : (
            <button data-testid="google-login-btn" onClick={handleGoogleLogin} className="flex items-center gap-1.5 bg-white text-black text-xs font-dm-sans font-bold px-3 py-1.5 rounded-full hover:bg-zinc-200 transition-all">
              <LogIn size={13} /> Sign in
            </button>
          )}
        </div>
      </nav>

      {/* Login Gate */}
      {!user && (
        <div className="fixed inset-0 z-30 bg-[#060608]/90 backdrop-blur-2xl flex flex-col items-center justify-center px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-sm">
            <div className="w-16 h-16 mx-auto mb-5 bg-gradient-to-tr from-[#00F0FF] to-[#7000FF] rounded-2xl flex items-center justify-center shadow-[0_0_40px_rgba(0,240,255,0.2)]">
              <Sparkles size={28} className="text-black" />
            </div>
            <h1 className="font-outfit font-black text-2xl sm:text-3xl text-white mb-2">Become An AI Founder</h1>
            <p className="text-zinc-500 font-dm-sans text-sm mb-6">Complete levels, submit evidence, earn your certificate.</p>
            <button data-testid="gate-login-btn" onClick={handleGoogleLogin} className="flex items-center gap-2.5 bg-white text-black font-dm-sans font-bold px-6 py-3 rounded-full hover:bg-zinc-200 transition-all mx-auto text-sm">
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Sign in with Google
            </button>
          </motion.div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <p className="text-xs font-dm-sans font-bold tracking-[0.2em] uppercase text-zinc-600 mb-1">AI Founder Program</p>
          <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tight" style={{ background: "linear-gradient(135deg, #00F0FF, #7000FF)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Mission Control
          </h1>
        </motion.div>

        {/* Certificate Banner */}
        {isCompleted && progress?.certificate_id && (
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="mb-8">
            <div className="relative overflow-hidden rounded-xl border border-[#00F0FF]/20 bg-gradient-to-r from-[#0A1A1F] to-[#0A0F1A] p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-5" data-testid="certificate-banner">
              <Trophy size={36} className="text-[#00F0FF] flex-shrink-0" />
              <div className="flex-1 text-center sm:text-left">
                <h2 className="font-outfit font-bold text-xl text-white">You're an AI Founder!</h2>
                <p className="text-zinc-500 font-dm-sans text-sm mt-1">Certificate ID: <span className="text-zinc-300 font-mono text-xs">{progress.certificate_id}</span></p>
              </div>
              <a href={`${API}/ai-founder/certificate/${progress.certificate_id}/pdf`} data-testid="download-certificate-btn" className="flex items-center gap-2 bg-[#00F0FF] text-black font-outfit font-bold px-5 py-2.5 rounded-lg hover:bg-white transition-colors text-sm flex-shrink-0">
                <Download size={15} /> Download Certificate
              </a>
            </div>
          </motion.div>
        )}

        {/* Horizontal Step Bar */}
        {!loading && levels.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="mb-8">
            <div className="flex items-center gap-1 bg-[#0A0A0C] border border-white/5 rounded-xl p-3 sm:p-4 overflow-x-auto">
              {levels.map((level, idx) => {
                const state = getLevelState(level.order);
                const isActive = activeLevel === level.level_id;
                const tasksInLevel = level.tasks?.length || 0;
                const approvedInLevel = (level.tasks || []).filter(t => getTaskStatus(t.task_id) === "approved").length;

                return (
                  <div key={level.level_id} className="flex items-center flex-1 min-w-0">
                    <button
                      onClick={() => state !== "locked" && setActiveLevel(isActive ? null : level.level_id)}
                      data-testid={`step-${level.order}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all w-full min-w-0 ${
                        isActive
                          ? "bg-white/5 border border-[#00F0FF]/30"
                          : state === "locked"
                          ? "opacity-40 cursor-not-allowed"
                          : "hover:bg-white/5 cursor-pointer"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-outfit font-bold ${
                        state === "completed"
                          ? "bg-gradient-to-tr from-[#00F0FF] to-[#7000FF] text-black"
                          : state === "active"
                          ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30"
                          : "bg-zinc-800 text-zinc-600 border border-zinc-700"
                      }`}>
                        {state === "completed" ? <Check size={14} strokeWidth={3} /> : state === "locked" ? <Lock size={12} /> : level.order}
                      </div>
                      <div className="min-w-0 hidden sm:block">
                        <p className={`text-sm font-dm-sans font-semibold truncate ${isActive ? "text-white" : "text-zinc-400"}`}>{level.title}</p>
                        <p className="text-xs text-zinc-600 font-dm-sans">{approvedInLevel}/{tasksInLevel} tasks</p>
                      </div>
                    </button>
                    {idx < levels.length - 1 && (
                      <div className={`w-6 h-px flex-shrink-0 mx-1 ${state === "completed" ? "bg-[#00F0FF]/40" : "bg-zinc-800"}`} />
                    )}
                  </div>
                );
              })}

              {/* Overall progress */}
              <div className="flex items-center gap-2 pl-3 border-l border-white/5 flex-shrink-0">
                <div className="text-right">
                  <p className="text-xs text-zinc-600 font-dm-sans">Progress</p>
                  <p className="text-lg font-outfit font-bold text-[#00F0FF]">{totalTasks > 0 ? Math.round((approvedTasks / totalTasks) * 100) : 0}%</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Task Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" /></div>
        ) : levels.length === 0 ? (
          <div className="text-center py-20 text-zinc-600 font-dm-sans">Program is being set up. Check back soon!</div>
        ) : (
          <AnimatePresence mode="wait">
            {levels.map(level => {
              const state = getLevelState(level.order);
              const isOpen = activeLevel === level.level_id;
              if (!isOpen) return null;

              return (
                <motion.div
                  key={level.level_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                >
                  {/* Level Header */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-outfit font-bold text-lg ${
                      state === "completed" ? "bg-gradient-to-tr from-[#00F0FF] to-[#7000FF] text-black" : "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20"
                    }`}>
                      {state === "completed" ? <Check size={20} strokeWidth={3} /> : level.order}
                    </div>
                    <div>
                      <h2 className="font-outfit font-bold text-xl text-white">{level.title}</h2>
                      <p className="text-sm text-zinc-500 font-dm-sans">{level.description}</p>
                    </div>
                  </div>

                  {/* Tasks List */}
                  <div className="space-y-2">
                    {level.tasks?.map((task, tIdx) => (
                      <TaskCard
                        key={task.task_id}
                        task={task}
                        status={getTaskStatus(task.task_id)}
                        submission={progress?.submissions?.[task.task_id]}
                        levelState={state}
                        onSubmitted={refreshProgress}
                        onViewFeedback={(t, s) => setDrawer({ task: t, submission: s })}
                        index={tIdx}
                      />
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}

        {/* Empty state when no level selected */}
        {!loading && levels.length > 0 && !activeLevel && (
          <div className="text-center py-16 text-zinc-600 font-dm-sans">
            <p className="text-sm">Select a level above to view tasks</p>
          </div>
        )}
      </div>

      {/* Feedback Drawer */}
      <FeedbackDrawer drawer={drawer} onClose={() => setDrawer(null)} />
    </div>
  );
}


/* ═══════════════════════════════════════════════════════
   Task Card — Full-width horizontal row
   ═══════════════════════════════════════════════════════ */
function TaskCard({ task, status, submission, levelState, onSubmitted, onViewFeedback, index }) {
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [urlInput, setUrlInput] = useState("");
  const [textInput, setTextInput] = useState("");
  const [uploadMode, setUploadMode] = useState("file");
  const fileRef = useRef(null);

  const s = STATUS[status] || STATUS.pending;
  const isRejected = status === "rejected";
  const isApproved = status === "approved";
  const isLocked = levelState === "locked";
  const canSubmit = !isApproved && !isLocked && status !== "submitted";

  const handleSubmit = async () => {
    const token = localStorage.getItem("ai_tools_token");
    if (!token) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("task_id", task.task_id);
      if (uploadMode === "file" && file) formData.append("evidence_file", file);
      else if (uploadMode === "url" && urlInput.trim()) formData.append("evidence_url", urlInput.trim());
      else if (uploadMode === "text" && textInput.trim()) formData.append("evidence_text", textInput.trim());
      else { alert("Please provide evidence"); setUploading(false); return; }

      await axios.post(`${API}/ai-founder/submit`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setShowUpload(false);
      setFile(null);
      setUrlInput("");
      setTextInput("");
      onSubmitted();
    } catch (err) {
      alert(err.response?.data?.detail || "Submission failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04 }}
    >
      {/* Main Row */}
      <div className={`flex items-center gap-4 px-5 py-4 rounded-lg border transition-all ${
        isApproved ? "bg-emerald-500/[0.03] border-emerald-500/10" :
        isRejected ? "bg-red-500/[0.03] border-red-500/10" :
        status === "submitted" ? "bg-amber-500/[0.03] border-amber-500/10" :
        "bg-white/[0.02] border-white/5 hover:border-white/10"
      }`} data-testid={`task-row-${task.task_id}`}>
        {/* Status dot */}
        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${s.dot}`} />

        {/* Task info */}
        <div className="flex-1 min-w-0">
          <p className={`text-base font-dm-sans font-medium ${isApproved ? "text-zinc-400" : "text-white"}`}>
            {task.title}
          </p>
          {task.description && (
            <p className="text-sm text-zinc-500 font-dm-sans mt-0.5">{task.description}</p>
          )}
        </div>

        {/* Status badge */}
        <span className={`hidden sm:inline-flex px-3 py-1.5 rounded-md text-xs font-dm-sans font-bold uppercase tracking-wider ${s.bg} ${s.text} border ${s.border}`}>
          {s.label}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {isApproved && submission && (
            <button
              onClick={() => onViewFeedback(task, submission)}
              className="flex items-center gap-1.5 text-xs font-dm-sans font-semibold text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/15 px-3 py-2 rounded-md transition-colors"
              data-testid={`view-history-${task.task_id}`}
            >
              <Eye size={13} /> History
            </button>
          )}
          {isRejected && (
            <button
              onClick={() => onViewFeedback(task, submission)}
              className="flex items-center gap-1.5 text-xs font-dm-sans font-semibold text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/15 px-3 py-2 rounded-md transition-colors"
              data-testid={`view-feedback-${task.task_id}`}
            >
              <Eye size={13} /> Feedback
            </button>
          )}
          {canSubmit && (
            <button
              data-testid={`submit-evidence-${task.task_id}`}
              onClick={() => setShowUpload(!showUpload)}
              className={`flex items-center gap-1.5 text-xs font-dm-sans font-bold px-3 py-2 rounded-md transition-all ${
                isRejected
                  ? "text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/15"
                  : "text-[#00F0FF] bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 border border-[#00F0FF]/15"
              }`}
            >
              {isRejected ? <><RotateCcw size={13} /> Resubmit</> : <><Upload size={13} /> Upload</>}
            </button>
          )}
          {status === "submitted" && (
            <span className="text-xs text-amber-400/60 font-dm-sans">Awaiting review</span>
          )}
        </div>
      </div>

      {/* Upload Panel */}
      <AnimatePresence>
        {showUpload && canSubmit && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-2 ml-6 bg-[#0A0A0E] border border-white/5 rounded-lg p-5">
              <div className="flex gap-2 mb-4">
                {[
                  { id: "file", icon: Upload, label: "File" },
                  { id: "url", icon: LinkIcon, label: "URL" },
                  { id: "text", icon: FileText, label: "Text" },
                ].map(m => (
                  <button key={m.id} onClick={() => setUploadMode(m.id)} data-testid={`upload-mode-${m.id}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-dm-sans font-semibold transition-colors ${
                      uploadMode === m.id ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20" : "bg-zinc-800/50 text-zinc-500 border border-zinc-800 hover:text-zinc-300"
                    }`}>
                    <m.icon size={13} /> {m.label}
                  </button>
                ))}
              </div>

              {uploadMode === "file" && (
                <div onClick={() => fileRef.current?.click()} data-testid="file-drop-area"
                  className="border border-dashed border-zinc-800 hover:border-[#00F0FF]/30 bg-zinc-900/30 rounded-lg p-6 text-center cursor-pointer transition-colors group">
                  <Upload size={22} className="mx-auto text-zinc-700 group-hover:text-[#00F0FF] transition-colors mb-2" />
                  <span className="text-sm text-zinc-600 group-hover:text-[#00F0FF] font-dm-sans">{file ? file.name : "Click to upload evidence"}</span>
                  <p className="text-xs text-zinc-700 font-dm-sans mt-1">Images, PDFs, Docs (max 50MB)</p>
                  <input ref={fileRef} type="file" className="hidden" data-testid="file-input" onChange={e => e.target.files?.[0] && setFile(e.target.files[0])} />
                </div>
              )}
              {uploadMode === "url" && (
                <input data-testid="url-input" type="url" value={urlInput} onChange={e => setUrlInput(e.target.value)} placeholder="https://..."
                  className="w-full bg-zinc-900 border border-zinc-800 focus:border-[#00F0FF]/30 rounded-lg px-4 py-3 text-base text-white font-dm-sans outline-none placeholder-zinc-700" />
              )}
              {uploadMode === "text" && (
                <textarea data-testid="text-input" value={textInput} onChange={e => setTextInput(e.target.value)} placeholder="Describe your evidence..."
                  className="w-full bg-zinc-900 border border-zinc-800 focus:border-[#00F0FF]/30 rounded-lg px-4 py-3 text-base text-white font-dm-sans outline-none placeholder-zinc-700 h-28 resize-none" />
              )}

              <div className="flex justify-end gap-3 mt-4">
                <button onClick={() => setShowUpload(false)} className="px-4 py-2 text-sm text-zinc-500 font-dm-sans hover:text-white transition-colors" data-testid="cancel-upload-btn">Cancel</button>
                <button data-testid="confirm-submit-btn" onClick={handleSubmit} disabled={uploading}
                  className="flex items-center gap-1.5 bg-[#00F0FF] text-black text-sm font-dm-sans font-bold px-5 py-2 rounded-md hover:bg-white transition-colors disabled:opacity-50">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {uploading ? "Submitting..." : "Submit Evidence"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}


/* ═══════════════════════════════════════════════════════
   Feedback Drawer — Slides in from right
   ═══════════════════════════════════════════════════════ */
function FeedbackDrawer({ drawer, onClose }) {
  if (!drawer) return null;
  const { task, submission } = drawer;
  const reviews = submission?.review_history || [];
  const lastReview = reviews[reviews.length - 1];
  const isApproved = submission?.status === "approved";
  const evidence = submission?.evidence || {};

  return (
    <AnimatePresence>
      {drawer && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-md bg-[#0A0A0E] border-l border-white/5 overflow-y-auto"
            data-testid="feedback-drawer"
          >
            {/* Drawer Header */}
            <div className="sticky top-0 bg-[#0A0A0E]/95 backdrop-blur-xl border-b border-white/5 px-6 py-5 flex items-center justify-between z-10">
              <div>
                <p className="text-xs font-dm-sans font-bold tracking-[0.15em] uppercase text-zinc-600">
                  {isApproved ? "Task History" : "Review Feedback"}
                </p>
                <h3 className="font-outfit font-bold text-white text-lg mt-0.5">{task.title}</h3>
              </div>
              <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-colors" data-testid="close-drawer">
                <X size={18} />
              </button>
            </div>

            <div className="px-6 py-6 space-y-6">
              {/* Status */}
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full ${isApproved ? "bg-emerald-400" : "bg-red-400"}`} />
                <span className={`text-sm font-dm-sans font-semibold ${isApproved ? "text-emerald-400" : "text-red-400"}`}>
                  {isApproved ? "Approved" : "Needs Revision"}
                </span>
                {lastReview?.reviewed_at && (
                  <span className="text-xs text-zinc-600 font-dm-sans ml-auto">{lastReview.reviewed_at.slice(0, 10)}</span>
                )}
              </div>

              {/* Submitted Evidence */}
              <div>
                <p className="text-xs font-dm-sans font-bold tracking-[0.12em] uppercase text-zinc-600 mb-2">Your Submission</p>
                <div className="bg-white/[0.03] border border-white/5 rounded-lg p-4">
                  {evidence.text && (
                    <p className="text-sm text-zinc-300 font-dm-sans leading-relaxed">{evidence.text}</p>
                  )}
                  {evidence.url && (
                    <a href={evidence.url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#00F0FF] font-dm-sans hover:underline break-all">
                      {evidence.url}
                    </a>
                  )}
                  {evidence.file_name && (
                    <a
                      href={`${API}/ai-founder/evidence/${submission.submission_id}/file`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-[#00F0FF] font-dm-sans hover:underline"
                    >
                      <FileText size={14} /> {evidence.file_name}
                    </a>
                  )}
                  {submission?.submitted_at && (
                    <p className="text-xs text-zinc-600 font-dm-sans mt-2">Submitted: {submission.submitted_at.slice(0, 10)}</p>
                  )}
                </div>
              </div>

              {/* Admin Feedback */}
              {lastReview?.feedback && (
                <div>
                  <p className="text-xs font-dm-sans font-bold tracking-[0.12em] uppercase text-zinc-600 mb-2">Admin Feedback</p>
                  <div className="bg-white/[0.03] border border-white/5 rounded-lg p-4">
                    <p className="text-sm text-zinc-300 font-dm-sans leading-relaxed">{lastReview.feedback}</p>
                  </div>
                </div>
              )}

              {/* AI Review */}
              {lastReview?.ai_review && (
                <div>
                  <p className="text-xs font-dm-sans font-bold tracking-[0.12em] uppercase text-purple-400 mb-2 flex items-center gap-1.5">
                    <Sparkles size={11} /> AI Review Suggestions
                  </p>
                  <div className="bg-purple-500/[0.04] border border-purple-500/10 rounded-lg p-4">
                    <div className="text-sm text-zinc-400 font-dm-sans leading-relaxed space-y-3">
                      {lastReview.ai_review.split('\n').filter(Boolean).map((line, i) => {
                        const trimmed = line.trim();
                        if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
                          return <p key={i} className="text-white font-semibold text-xs uppercase tracking-wider mt-2">{trimmed.replace(/\*\*/g, '')}</p>;
                        }
                        if (/^\*\*\d/.test(trimmed) || /^\d+\./.test(trimmed)) {
                          return <p key={i} className="text-zinc-200 font-semibold text-sm mt-2">{trimmed.replace(/\*\*/g, '')}</p>;
                        }
                        if (trimmed.startsWith('*') || trimmed.startsWith('-')) {
                          const clean = trimmed.replace(/^\*\s*/, '').replace(/^-\s*/, '').replace(/\*\*/g, '');
                          return (
                            <div key={i} className="flex gap-2 items-start">
                              <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 flex-shrink-0" />
                              <p className="text-sm text-zinc-400">{clean}</p>
                            </div>
                          );
                        }
                        return <p key={i} className="text-sm text-zinc-400">{trimmed.replace(/\*\*/g, '')}</p>;
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Screenshot */}
              {lastReview?.screenshot_path && (
                <div>
                  <p className="text-xs font-dm-sans font-bold tracking-[0.12em] uppercase text-zinc-600 mb-2 flex items-center gap-1.5">
                    <ImageIcon size={11} /> Admin Screenshot
                  </p>
                  <img
                    src={`${API}/ai-founder/feedback-image?path=${encodeURIComponent(lastReview.screenshot_path)}`}
                    alt="Feedback"
                    className="w-full rounded-lg border border-white/5"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}

              {/* Full Review History */}
              {reviews.length > 0 && (
                <div>
                  <p className="text-xs font-dm-sans font-bold tracking-[0.12em] uppercase text-zinc-600 mb-2">
                    Review History ({reviews.length} {reviews.length === 1 ? "review" : "reviews"})
                  </p>
                  <div className="space-y-2">
                    {[...reviews].reverse().map((r, i) => (
                      <div key={i} className="bg-white/[0.02] border border-white/5 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className={`w-2 h-2 rounded-full ${r.action === "approve" ? "bg-emerald-400" : "bg-red-400"}`} />
                          <span className={`text-xs font-dm-sans font-semibold ${r.action === "approve" ? "text-emerald-400" : "text-red-400"}`}>
                            {r.action === "approve" ? "Approved" : "Rejected"}
                          </span>
                          <span className="text-xs text-zinc-700 font-dm-sans ml-auto">{r.reviewed_at?.slice(0, 10)}</span>
                        </div>
                        {r.feedback && <p className="text-sm text-zinc-400 font-dm-sans">{r.feedback}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!lastReview?.feedback && !lastReview?.ai_review && !isApproved && (
                <div className="bg-white/[0.02] border border-white/5 rounded-lg p-4 text-center">
                  <p className="text-sm text-zinc-500 font-dm-sans">Please revise your submission and resubmit.</p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
